/**
 * ChatArea Component
 * 
 * Scrollable message list container with iMessage-style bubbles.
 * Each message gets its own bubble (self + others).
 * 
 * Updated December 11, 2025 (Issue #608): Removed giant card wrapper,
 * using individual ChatMessageRow components for each message.
 */

import { type Message } from './ChatMessage';
import { ChatMessageRow } from './ChatMessageRow';
import { NoMessagesEmptyState } from './EmptyStatePresets';

/**
 * Format a date separator label (#1516)
 * "Today", "Yesterday", or "Mon, Mar 15"
 */
function formatDateSeparator(timestamp: number): string {
  const date = new Date(timestamp * 1000);
  const now = new Date();

  const dateDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffDays = Math.round((today.getTime() - dateDay.getTime()) / 86400000);

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';

  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

/** Get date string (YYYY-MM-DD) from unix seconds for comparison */
function getDateKey(timestamp: number): string {
  const d = new Date(timestamp * 1000);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

interface ChatAreaProps {
  messages: Message[];
  clubId?: string; // #920: For message actions (delete/report)
  roomId?: string; // #1222: For query invalidation after delete
  // #1611: ID of the first message that is unread on this device, or null
  // when there's no unread state (or it's been suppressed by the boundary
  // check in ClubChatPage). When non-null, an "Unread" divider renders
  // immediately above the matching message.
  firstUnreadId?: string | null;
  // #989: room write access — gates reaction toggling. Defaults to FALSE
  // (fail closed, George r1): the parent must pass the room's write capability
  // explicitly — a forgotten prop must not open reaction chrome.
  canReact?: boolean;
  // #2708: leader/owner pin capability for the active room (can_pin).
  canPin?: boolean;
  // #2708: id of the room's currently pinned message (drives Pin↔Unpin).
  pinnedMessageId?: string | null;
  onScroll?: (isScrolled: boolean) => void;
  onSendMessage?: () => void;
}

export default function ChatArea({ messages, clubId, roomId, firstUnreadId, canReact = false, canPin = false, pinnedMessageId, onScroll, onSendMessage }: ChatAreaProps) {
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const scrollTop = e.currentTarget.scrollTop;
    onScroll?.(scrollTop > 20);
  };

  if (messages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center px-4 pt-4 bg-oro-bg">
        <NoMessagesEmptyState
          variant="compact"
          onAction={onSendMessage}
        />
      </div>
    );
  }

  return (
    <div
      className="flex-1 py-4 bg-oro-bg"
      onScroll={handleScroll}
    >
      {messages.map((message, index) => {
        // Insert date separator when day changes between messages (#1516)
        const prevMessage = index > 0 ? messages[index - 1] : null;
        const showDateSeparator = message.timestampSeconds && (
          !prevMessage?.timestampSeconds ||
          getDateKey(message.timestampSeconds) !== getDateKey(prevMessage.timestampSeconds)
        );
        // #1611: Render the unread divider immediately above the first unread
        // message. The DOM id on the wrapper anchors scrollIntoView from the
        // first-load layout effect in ClubChatPage.
        const showUnreadDivider = !!firstUnreadId && firstUnreadId === message.id;

        return (
          <div key={message.id} id={`msg-${message.id}`}>
            {showDateSeparator && (
              <div className="flex items-center justify-center py-3 px-4">
                <div className="px-3 py-1 bg-oro-surface border border-oro-border rounded-full">
                  <span className="text-[11px] font-medium text-text-muted">
                    {formatDateSeparator(message.timestampSeconds!)}
                  </span>
                </div>
              </div>
            )}
            {showUnreadDivider && (
              <div
                className="flex items-center gap-2 py-2 px-4"
                role="separator"
                aria-label="Unread messages"
              >
                <div className="flex-1 h-px bg-accent/40" />
                <span className="text-[11px] font-medium text-accent uppercase tracking-wider">
                  Unread
                </span>
                <div className="flex-1 h-px bg-accent/40" />
              </div>
            )}
            <ChatMessageRow
              message={message}
              isOwn={message.type === 'self'}
              clubId={clubId}
              roomId={roomId}
              canReact={canReact}
              canPin={canPin}
              isPinnedMessage={!!pinnedMessageId && message.id === pinnedMessageId}
            />
          </div>
        );
      })}
    </div>
  );
}
